import type { StateCreator } from 'zustand';
import { imageUploadService } from '@/shared/lib/services/image-upload.service';
import { createLogger } from '@/shared/lib/logger';
import type {
  ProcessedFile,
  UploadResult,
} from '@/shared/lib/services/image-upload.service';
import type { UploadType } from '@/shared/constants';
import type { UploadPolicy } from '@/types/project-reference';
import {
  generateBatchId,
  generateItemId,
  type UploadData,
  type UploadItem,
} from './upload.store.internals';

const logger = createLogger('UploadStore');

/**
 * Public action surface. Audit (2026-05-11, re-trimmed 2026-06-10) showed only
 * `createBatch / uploadBatch / uploadSingle / getTotalProgress` are called
 * from outside the store — the legacy queue-level actions (pause/resume/
 * cancel/retry/process/clearCompleted/Failed/getXItems/canUpload/...), the
 * write-only `settings` surface and the never-called `reset` were dropped.
 * `addToQueue / updateProgress` stay because they're called via `get()` from
 * inside `uploadSingle`.
 */
export interface UploadActions {
  // Public
  createBatch: (
    files: File[],
    type: UploadType,
    mainAngleIndex?: number
  ) => string;
  uploadBatch: <TConfirmed = unknown>(
    batchId: string,
    apiCallback: (
      files: ProcessedFile[],
      mainIndex?: number
    ) => Promise<Array<{ id: string; uploadPolicy: UploadPolicy }>>,
    confirm?: (id: string) => Promise<TConfirmed>
  ) => Promise<UploadResult<TConfirmed>[]>;
  uploadSingle: <TConfirmed = unknown>(
    file: File,
    type: UploadType,
    apiCallback: (
      file: ProcessedFile
    ) => Promise<{ id: string; uploadPolicy: UploadPolicy }>,
    confirm?: (id: string) => Promise<TConfirmed>
  ) => Promise<UploadResult<TConfirmed>>;
  getTotalProgress: () => number;

  // Internal — called via `get()` from other actions, must live on the store
  addToQueue: (files: File[], type: UploadType) => string[];
  updateProgress: (itemId: string, progress: number) => void;
}

type UploadStore = UploadData & UploadActions;

export const createActions: StateCreator<
  UploadStore,
  [['zustand/immer', never]],
  [],
  UploadActions
> = (set, get) => ({
  // ===== Public actions =====

  createBatch: (files, type, mainAngleIndex) => {
    const batchId = generateBatchId();
    const items: UploadItem[] = files.map((file) => ({
      id: generateItemId(),
      file,
      type,
      status: 'pending' as const,
      progress: 0,
      retryCount: 0,
    }));

    set((state) => {
      // New upload session: drop finished items from previous sessions so
      // getTotalProgress reflects only the current upload (stale lists made
      // the average drift across batches). Guarded so a concurrent in-flight
      // upload from another surface is never clobbered.
      if (!state.ui.loading.upload) {
        state.completedUploads = [];
        state.failedUploads = [];
      }

      state.batches[batchId] = {
        id: batchId,
        items,
        mainAngleIndex,
        totalProgress: 0,
        status: 'pending',
        createdAt: Date.now(),
      };

      state.ui.currentBatchId = batchId;
      state.ui.showProgressBar = true;
    });

    return batchId;
  },

  uploadBatch: async (batchId, apiCallback, confirm) => {
    const batch = get().batches[batchId];
    if (!batch) throw new Error('Batch not found');

    set((state) => {
      const target = state.batches[batchId];
      if (target) target.status = 'processing';
      state.ui.loading.upload = true;
      state.ui.error.upload = null;
    });

    try {
      const results = await imageUploadService.uploadBatch(
        batch.items.map((item) => item.file),
        {
          type: batch.items[0]?.type ?? 'angle',
          maxFiles: batch.items.length,
          // Real per-file S3 byte progress → per-item progress + batch total.
          onFileProgress: (fileIndex, progress) => {
            set((state) => {
              const target = state.batches[batchId];
              if (!target) return;
              const item = target.items[fileIndex];
              if (item) {
                item.progress = progress.percentage;
                item.status =
                  progress.percentage >= 100 ? 'completed' : 'uploading';
              }
              target.totalProgress = Math.round(
                target.items.reduce((sum, it) => sum + it.progress, 0) /
                  target.items.length
              );
            });
          },
          onCleanupFailure: async (id) => {
            logger.info('Cleaning up failed upload:', id);
          },
        },
        apiCallback,
        confirm
      );

      set((state) => {
        const target = state.batches[batchId];
        if (target) {
          target.status = 'completed';
          target.completedAt = Date.now();
          target.totalProgress = 100;
        }

        state.ui.loading.upload = false;
        state.stats.totalUploaded += results.length;
      });

      return results;
    } catch (error) {
      set((state) => {
        const target = state.batches[batchId];
        if (target) target.status = 'failed';

        state.ui.loading.upload = false;
        state.ui.error.upload =
          error instanceof Error ? error.message : 'Upload failed';
        state.stats.totalFailed += batch.items.length;
      });

      throw error;
    }
  },

  uploadSingle: async (file, type, apiCallback, confirm) => {
    // New upload session — see createBatch. Also detach any stale batch
    // pointer so progress readers aggregate the live queue item, not a
    // finished batch.
    set((state) => {
      if (!state.ui.loading.upload) {
        state.completedUploads = [];
        state.failedUploads = [];
        state.ui.currentBatchId = null;
      }
    });

    const itemId = get().addToQueue([file], type)[0];
    if (!itemId) {
      throw new Error('Failed to create upload item');
    }

    set((state) => {
      state.ui.loading.upload = true;
      state.ui.error.upload = null;
      const item = state.queue.find((i) => i.id === itemId);
      if (item) {
        item.status = 'processing';
        item.startedAt = Date.now();
      }
    });

    try {
      const result = await imageUploadService.uploadSingle(
        file,
        {
          type,
          onProgress: (progress) => {
            get().updateProgress(itemId, progress.percentage);
          },
          onCleanupFailure: async (id) => {
            logger.info('Cleaning up failed upload:', id);
          },
        },
        apiCallback,
        confirm
      );

      set((state) => {
        const item = state.queue.find((i) => i.id === itemId);
        if (item) {
          item.status = 'completed';
          item.completedAt = Date.now();
          item.result = result;
          item.progress = 100;

          state.completedUploads.push(item);
          state.queue = state.queue.filter((i) => i.id !== itemId);
        }

        state.ui.loading.upload = false;
        state.stats.totalUploaded++;
        state.stats.lastUploadTime = Date.now();
      });

      return result;
    } catch (error) {
      set((state) => {
        const item = state.queue.find((i) => i.id === itemId);
        if (item) {
          item.status = 'failed';
          item.error =
            error instanceof Error ? error.message : 'Upload failed';

          state.failedUploads.push(item);
          state.queue = state.queue.filter((i) => i.id !== itemId);
        }

        state.ui.loading.upload = false;
        state.ui.error.upload =
          error instanceof Error ? error.message : 'Upload failed';
        state.stats.totalFailed++;
      });

      throw error;
    }
  },

  getTotalProgress: () => {
    const state = get();

    // Active batch wins — aggregate real per-item byte progress.
    const batchId = state.ui.currentBatchId;
    if (batchId) {
      const batch = state.batches[batchId];
      if (batch && batch.items.length > 0) {
        return Math.round(
          batch.items.reduce((sum, item) => sum + item.progress, 0) /
            batch.items.length
        );
      }
    }

    // Single-upload session (queue) — finished lists are cleared per-session
    // at upload start, so the average covers only the current session.
    const allItems = [
      ...state.queue,
      ...state.completedUploads,
      ...state.failedUploads,
    ];
    if (allItems.length === 0) return 0;

    const totalProgress = allItems.reduce(
      (sum, item) => sum + item.progress,
      0
    );
    return Math.round(totalProgress / allItems.length);
  },

  // ===== Internal helpers (must live on the store; called via get()) =====

  addToQueue: (files, type) => {
    const itemIds: string[] = [];

    set((state) => {
      files.forEach((file) => {
        const id = generateItemId();
        itemIds.push(id);

        state.queue.push({
          id,
          file,
          type,
          status: 'pending',
          progress: 0,
          retryCount: 0,
        });
      });

      state.ui.showProgressBar = true;
    });

    return itemIds;
  },

  updateProgress: (itemId, progress) => {
    set((state) => {
      const item = state.queue.find((i) => i.id === itemId);
      if (item) {
        item.progress = progress;
        item.status = progress === 100 ? 'completed' : 'uploading';
      }
    });
  },
});
